import { Table, Tooltip, Col } from 'antd'
import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'
import { PerEmpty } from '../../components/custom_components/Empty'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { useAmountAndCurrency } from '../../shared/useAmountAndCurrency'
import { ObjectivePayment, ObjectivePaymentManualDeductionReason, ObjectiveDto, Currency } from '../../types'
import './objectivePayment.less'
import { retrieveCurrencyConversionRate } from '../../shared/currencyConversionUtil'
import { IconIndicator } from '../../components/custom_components/IconIndicator/IconIndicator'
import { useCurrency } from '../../context/currency-context'
import { round2digits } from '../appraisal/appraisal-flow/meeting/util/paymentsUtil'

interface PlannedPaymenTabletProps {
  objectives: ObjectiveDto[]
  meetingId?: number
  handleObjectiveClicked?: (isManagerPreparation: boolean, objective: ObjectiveDto) => void
  manualDeduction: ObjectivePayment
  handleDeductionDelete: () => void
  handleDeductionEdit: () => void
}

interface PlannedPaymentTableItem {
  id: string
  title: ReactNode | string
  amount: ReactNode
  actions?: ReactNode | string
  reached?: ReactNode
  currencyErrorText?: string
}

export const PlannedPaymentTable: FC<PlannedPaymenTabletProps> = ({
  objectives,
  handleObjectiveClicked,
  manualDeduction,
  handleDeductionDelete,
  handleDeductionEdit,
}) => {
  const { user } = useAuth()
  const [t] = useTranslation()

  const { tableActionButtonsRowWidth } = tableActionButtons()

  const { formatCurrency, formatAmountAsPercentage, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const { currency: currentContractCurrency, currencyConversionData } = useCurrency()

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      objectives
        .filter((obj): boolean => {
          if (obj.currency! !== currentContractCurrency) {
            const conversionRate: number | undefined = retrieveCurrencyConversionRate(
              obj.currency!,
              currentContractCurrency,
              currencyConversionData,
            )
            if (!conversionRate) {
              return true
            }
          }
          return false
        })
        .map((obj) => obj.currency!),
    ),
  ]

  const getTitle = (title: string, objectiveCurrency?: Currency): JSX.Element => {
    if (!objectiveCurrency || objectiveCurrency === currentContractCurrency) {
      return <PerText fontWeight="bold">{title}</PerText>
    }
    return (
      <PerFlexRow align="middle">
        <Col span={3}>
          <IconIndicator
            icon="warning_fill"
            iconSize={12}
            background="error"
            size="l"
            tooltip={t('currencyConversion.tooltips.tableWarning')}
          />
        </Col>
        <Col span={20}>
          <PerText fontWeight="bold">{title}</PerText>
        </Col>
      </PerFlexRow>
    )
  }

  const getObjectiveReached = (objective: ObjectiveDto): JSX.Element => {
    const percentage =
      objective.achievedAmount !== undefined && objective.achievedAmount !== null && objective.targetAmount
        ? formatAmountAsPercentage(objective.achievedAmount / objective.targetAmount, true, false)
        : 0
    return <PerText>{percentage}%</PerText>
  }

  const getObjectiveAmount = (amount: number, currency: Currency): JSX.Element => {
    if (!!amount && currency !== currentContractCurrency) {
      const conversionRate: number | undefined = retrieveCurrencyConversionRate(currency, currentContractCurrency, currencyConversionData)
      return (
        <PerFlexRow direction="column">
          <Tooltip
            title={
              unknownCurrenciesForConversion.includes(currency)
                ? t('currencyConversion.tooltips.tableConversionWarning', {
                    startingCurrency: currency,
                    targetCurrency: currentContractCurrency,
                  })
                : t('currencyConversion.tooltips.tableWarning')
            }
            placement="right"
          >
            <PerFlexRow>
              <PerText>{formatCurrency(amount, currency)}</PerText>
            </PerFlexRow>
            <PerFlexRow>
              <PerText fontWeight={conversionRate ? undefined : 'bold'} fontSize="xs" mt="xxs" color={conversionRate ? undefined : 'error'}>
                {calculateAndFormatNewTargetAmount(amount, currentContractCurrency, conversionRate)}
              </PerText>
            </PerFlexRow>
          </Tooltip>
        </PerFlexRow>
      )
    } else {
      return <PerText>{formatCurrency(amount, currency)}</PerText>
    }
  }

  const getObjectiveActions = (objective: ObjectiveDto): JSX.Element | undefined => {
    if (objective.currency !== currentContractCurrency) {
      return undefined
    }
    return (
      <PerButtonCircle
        icon="view_fill"
        color="primary"
        tooltip={t('tooltip.view')}
        variant="bold"
        onClick={() => handleObjectiveClicked && handleObjectiveClicked(false, objective)}
        disabled={objective.currency !== currentContractCurrency}
      />
    )
  }

  const getDeductionTitle = (reason: ObjectivePaymentManualDeductionReason): JSX.Element => {
    return (
      <>
        <PerText appearance="label-small">{t('appraisalMeeting.stepPayment.deduction.title')}</PerText>
        <PerText>{t(`appraisalMeeting.stepPayment.deduction.reason.${reason}`)}</PerText>
      </>
    )
  }

  const getDeductionAmount = (amount: number): JSX.Element => {
    return <PerText>- {formatCurrency(amount, currentContractCurrency)}</PerText>
  }

  const getDeductionActions = () => {
    return (
      <>
        <PerButtonCircle
          onClick={(e) => {
            e.preventDefault()
            handleDeductionEdit()
          }}
          color="primary"
          icon="edit_fill"
          variant="bold"
          tooltip={t('tooltip.edit')}
          mr="xxs"
          data-e2e-edit-objective-payment-edit-deduction
        />
        <PerButtonCircle
          onClick={(e) => {
            e.preventDefault()
            handleDeductionDelete()
          }}
          color="error"
          icon="delete_fill"
          variant="bold"
          tooltip={t('tooltip.delete')}
          data-e2e-edit-objective-payment-delete-deduction
        />
      </>
    )
  }

  const generateTableItems = (): PlannedPaymentTableItem[] => {
    const objectiveRows = objectives.map((objective, index) => {
      let conversionRate: number | undefined = undefined
      if (objective.currency !== currentContractCurrency) {
        conversionRate = retrieveCurrencyConversionRate(objective.currency!, currentContractCurrency, currencyConversionData)
      }
      return {
        id: `${index}_${objective.id}_${objective.globalId}`,
        title: getTitle(objective.title, !!objective.currency ? objective.currency : undefined),
        amount: getObjectiveAmount(
          !!objective.achievedAmount ? objective.achievedAmount : 0,
          !!objective.currency ? objective.currency : currentContractCurrency,
        ),
        actions: getObjectiveActions(objective),
        reached: getObjectiveReached(objective),
        currencyErrorText:
          objective.currency !== currentContractCurrency && conversionRate === undefined
            ? t('currencyConversion.tooltips.conversionError', {
                startingCurrency: objective.currency,
                targetCurrency: currentContractCurrency,
              })
            : undefined,
      }
    })
    let deductionRow: PlannedPaymentTableItem[] = []
    if (manualDeduction.deduction && manualDeduction.deductionReason) {
      deductionRow = [
        {
          id: `${manualDeduction.id}_deduction`,
          title: getDeductionTitle(manualDeduction.deductionReason),
          amount: getDeductionAmount(manualDeduction.deduction),
          actions: getDeductionActions(),
        } as PlannedPaymentTableItem,
      ]
    }
    return [...objectiveRows, ...deductionRow]
  }

  const tableItems: PlannedPaymentTableItem[] = generateTableItems()

  const columns = [
    {
      key: 'title',
      title: t('appraisalMeeting.stepPayment.table.heading'),
      dataIndex: 'title',
      className: 'minWidth-column-xl',
      render: (dataIndex: JSX.Element) => <PerText fontWeight="bold">{dataIndex}</PerText>,
    },
    {
      key: 'monetaryReached',
      title: t('okr.objective.monetary.reached'),
      dataIndex: 'reached',
      className: 'textAlign-left minWidth-column-xl',
      render: (dataIndex: JSX.Element) => dataIndex,
    },
    {
      key: 'monetaryPaid',
      title: t('okr.objective.monetary.toBePaid'),
      dataIndex: 'amount',
      className: 'textAlign-right minWidth-column-xl',
      render: (dataIndex: JSX.Element) => dataIndex,
    },
    {
      key: 'action',
      title: t('okr.objective.actions'),
      className: 'textAlign-left minWidth-column-s',
      dataIndex: 'actions',
      width: tableActionButtonsRowWidth(2),
      render: (actions: JSX.Element | undefined) => actions,
    },
  ]

  const footer = () => {
    const targetBonusToBePaid = !!unknownCurrenciesForConversion.length
      ? 0
      : objectives.reduce((sum: number, obj: ObjectiveDto) => {
          if (obj.currency !== currentContractCurrency) {
            if (!obj.achievedAmount || unknownCurrenciesForConversion.includes(obj.currency!)) {
              return sum
            }
            const conversionRate: number | undefined = retrieveCurrencyConversionRate(
              obj.currency!,
              currentContractCurrency,
              currencyConversionData,
            )
            if (conversionRate === undefined) {
              return sum
            } else {
              return sum + Number((obj.achievedAmount * conversionRate).toFixed(2))
            }
          }
          return sum + (obj.achievedAmount || 0)
        }, 0)
    const deductionAmount = manualDeduction && manualDeduction.deduction ? manualDeduction.deduction : 0
    const toPay = round2digits(targetBonusToBePaid - deductionAmount)

    return (
      <PerFlexRow justify="space-between">
        <PerText fontSize="s">{t('appraisalMeeting.stepPayment.table.footer')}</PerText>
        <PerText
          fontWeight={!!unknownCurrenciesForConversion.length ? 'bold' : undefined}
          tooltip={
            !!unknownCurrenciesForConversion.length
              ? t('currencyConversion.tooltips.unknownConversionRate', {
                  currentContractCurrency,
                  unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                })
              : undefined
          }
          color={toPay < 0 || !!unknownCurrenciesForConversion.length ? 'error' : 'primary'}
          className="per-objectivePayment-table-footer_amount"
        >
          {!!unknownCurrenciesForConversion.length && calculateAndFormatNewTargetAmount(toPay, currentContractCurrency)}
          {!unknownCurrenciesForConversion.length && formatCurrency(toPay, currentContractCurrency)}
        </PerText>
      </PerFlexRow>
    )
  }

  return (
    <>
      <Table
        dataSource={tableItems.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        pagination={false}
        footer={tableItems.length !== 0 ? () => footer() : undefined}
        locale={{
          emptyText: (
            <div data-e2e-no-oneoffpayment-scheduled>
              <PerEmpty
                info={t('appraisalMeeting.stepPayment.noPaymentsDescription')}
                info2={t('appraisalMeeting.stepPayment.noPaymentsHint')}
              />
            </div>
          ),
        }}
      />
    </>
  )
}
